// src/components/AnnualLeaves.js
import React, { useState, useEffect, useRef } from "react";
import {
  Form, Button, Alert, Card, Row, Col,
  Spinner, Modal, Tabs, Tab, Badge,
  Container, InputGroup, Table,
  Dropdown, OverlayTrigger, Tooltip
} from "react-bootstrap";
import { io } from "socket.io-client";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaCheckCircle, FaExclamationCircle,
  FaCalendarAlt, FaUserPlus, FaCog, FaSave,
  FaUsers, FaLeaf, FaCalendarPlus,
  FaChevronLeft, FaChevronRight, FaPencilAlt, FaTrash,
  FaBuilding, FaPlus, FaCheck, FaTimes, FaFilter,
  FaCalendarDay, FaMapMarkerAlt, FaRegCalendarCheck,
  FaEllipsisV, FaEye, FaDownload, FaSort,
  FaSortUp, FaSortDown
} from "react-icons/fa";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Modern Multi-select dropdown for Locations
const ModernLocationSelect = ({ options = [], selectedLocations = [], setSelectedLocations }) => {
  const [open, setOpen] = useState(false);

  const toggleOption = (opt) => {
    if (selectedLocations.includes(opt)) {
      setSelectedLocations(selectedLocations.filter((l) => l !== opt));
    } else {
      setSelectedLocations([...selectedLocations, opt]);
    }
  };

  const toggleSelectAll = () => {
    if (selectedLocations.length === options.length) {
      setSelectedLocations([]);
    } else {
      setSelectedLocations([...options]);
    }
  };

  return (
    <div className="position-relative">
      <div
        className="border rounded p-2 d-flex justify-content-between align-items-center"
        style={{
          cursor: "pointer",
          background: "white",
          borderColor: "#dee2e6",
          minHeight: "38px"
        }}
        onClick={() => setOpen(!open)}
      >
        <div className="d-flex flex-wrap gap-1">
          {selectedLocations.length > 0 ? (
            selectedLocations.map((loc) => (
              <Badge
                key={loc}
                bg="primary"
                className="d-flex align-items-center"
                style={{ fontSize: "0.8rem" }}
              >
                <FaBuilding className="me-1" size={10} />
                {loc}
              </Badge>
            ))
          ) : (
            <span className="text-muted">Select locations...</span>
          )}
        </div>
        <FaChevronRight
          className={`text-muted transition-all ${open ? "rotate-90" : ""}`}
          style={{ transition: "transform 0.2s" }}
        />
      </div>

      {open && (
        <div
          className="border rounded bg-white shadow-lg position-absolute p-3"
          style={{
            zIndex: 1000,
            width: "100%",
            top: "100%",
            left: 0,
            marginTop: "5px",
            maxHeight: "300px",
            overflowY: "auto"
          }}
        >
          <div className="mb-2">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={toggleSelectAll}
              className="w-100 mb-2"
            >
              {selectedLocations.length === options.length ? "Deselect All" : "Select All"}
            </Button>
          </div>
          {options.map((office) => (
            <div
              key={office}
              className="d-flex align-items-center mb-2 p-2 rounded hover-bg"
              style={{ cursor: "pointer" }}
              onClick={() => toggleOption(office)}
            >
              <div className={`me-2 ${selectedLocations.includes(office) ? "text-primary" : "text-muted"}`}>
                {selectedLocations.includes(office) ? (
                  <FaCheckCircle />
                ) : (
                  <div style={{ width: "16px", height: "16px", border: "2px solid #dee2e6", borderRadius: "3px" }} />
                )}
              </div>
              <FaBuilding className="me-2 text-muted" />
              <span className={selectedLocations.includes(office) ? "fw-semibold" : ""}>
                {office}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Modern Table Row Component
const HolidayTableRow = ({ holiday, offices, onEdit, onDelete, isLocked }) => {
  const hd = new Date(holiday.holiday_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const holidayDate = new Date(hd.getFullYear(), hd.getMonth(), hd.getDate());
  const isPast = holidayDate < today;

  const formattedDate = hd.toLocaleDateString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const getStatusColor = () => {
    if (isPast) return "secondary";
    return holiday.approval_status === "Approved" ? "success" : "warning";
  };

  const getStatusText = () => {
    if (isPast) return "Completed";
    return holiday.approval_status === "Approved" ? "Approved" : "Pending";
  };

  return (
    <tr className={isPast ? "table-light" : ""}>
      <td className="align-middle">
        <div className="d-flex align-items-center">
          <div className="me-3">
            <FaCalendarDay className="text-primary" />
          </div>
          <div>
            <div className="fw-bold">{holiday.holiday_name}</div>
            <div className="text-muted small">{formattedDate}</div>
          </div>
        </div>
      </td>

      <td className="align-middle">
        <div className="d-flex flex-wrap gap-1">
          {holiday.locations.map((loc) => (
            <Badge key={loc} bg="light" text="dark" className="border d-flex align-items-center">
              <FaMapMarkerAlt className="me-1" size={10} />
              {loc}
            </Badge>
          ))}
        </div>
      </td>

      <td className="align-middle text-center">
        <Badge bg={getStatusColor()} className="px-3 py-2">
          {getStatusText()}
        </Badge>
      </td>

      <td className="align-middle">
        <div className="d-flex justify-content-center gap-2">
          <OverlayTrigger overlay={<Tooltip>Edit Holiday</Tooltip>}>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => onEdit(holiday)}
              disabled={isLocked}
              className="rounded-circle"
              style={{ width: "36px", height: "36px" }}
            >
              <FaPencilAlt />
            </Button>
          </OverlayTrigger>
          <OverlayTrigger overlay={<Tooltip>Delete Holiday</Tooltip>}>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => onDelete(holiday)}
              disabled={isLocked}
              className="rounded-circle"
              style={{ width: "36px", height: "36px" }}
            >
              <FaTrash />
            </Button>
          </OverlayTrigger>
        </div>
      </td>
    </tr>
  );
};

const AnnualLeaves = () => {
  // State for Annual Leaves (Tab 1)
  const [allocatedUnplannedLeave, setAllocatedUnplannedLeave] = useState("");
  const [allocatedPlannedLeave, setAllocatedPlannedLeave] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [enableSaveButton, setEnableSaveButton] = useState(false);
  const [saveButtonError, setSaveButtonError] = useState("");
  const [allocatedLeaves, setAllocatedLeaves] = useState({
    unplanned: "",
    planned: "",
  });

  // State for New Joiner Leaves (Tab 2)
  const [employees, setEmployees] = useState([]);
  const [addEmployeeId, setAddEmployeeId] = useState("");
  const [joinDate, setJoinDate] = useState("");
  const [addAllocatedPlanned, setAddAllocatedPlanned] = useState("");
  const [addAllocatedUnplanned, setAddAllocatedUnplanned] = useState("");
  const [message, setMessage] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // State for Public Holidays (Tab 3)
  const [holidays, setHolidays] = useState([]);
  const [offices, setOffices] = useState([]);
  const [showOfficeModal, setShowOfficeModal] = useState(false);
  const [newOfficeName, setNewOfficeName] = useState("");
  const [showOfficeConfirm, setShowOfficeConfirm] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newHoliday, setNewHoliday] = useState({ date: "", name: "", locations: [] });
  const [editing, setEditing] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [dateError, setDateError] = useState("");
  const [showDateError, setShowDateError] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const [sortDirection, setSortDirection] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");

  const socketRef = useRef(null);
  const [activeTab, setActiveTab] = useState("holidays");

  const HOLIDAY_API = `${API_URL}/api/holidays`;
  const storedUser = JSON.parse(localStorage.getItem("user"));

  // Common calculations
  const totalLeaves = (parseInt(allocatedUnplannedLeave, 10) || 0) + (parseInt(allocatedPlannedLeave, 10) || 0);
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

  // Helper functions
  const handleValueChange = (val) => {
    if (val === "" || val === null || val === undefined) return "";
    const num = parseInt(val, 10);
    if (!isNaN(num)) return num > 999 ? "999" : val.toString();
    return "";
  };

  const clearValidationError = (fieldName) => {
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  };

  // Annual Leaves Tab Functions
  const handleSaveAnnualLeaves = () => {
    const unplanned = parseInt(allocatedUnplannedLeave, 10) || 0;
    const planned = parseInt(allocatedPlannedLeave, 10) || 0;

    if (unplanned <= 0 || planned <= 0) {
      setErrorMsg("Please enter valid numbers greater than 0 for both leave fields.");
      setShowErrorModal(true);
      return;
    }
    setShowConfirmModal(true);
  };

  const confirmSaveAnnualLeaves = async () => {
    setShowConfirmModal(false);
    const unplanned = parseInt(allocatedUnplannedLeave, 10) || 0;
    const planned = parseInt(allocatedPlannedLeave, 10) || 0;

    try {
      const res = await fetch(`${API_URL}/api/employee-leaves`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          year: selectedYear,
          allocatedUnplannedLeave: unplanned,
          allocatedPlannedLeave: planned,
          remainingUnplannedLeave: unplanned,
          remainingPlannedLeave: planned,
        }),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || res.status);
      }

      toast.success(`Leaves saved for ${selectedYear}! Unplanned: ${unplanned}, Planned: ${planned}, Total: ${totalLeaves}`);

      setAllocatedLeaves({
        unplanned: parseInt(unplanned.toString()),
        planned: parseInt(planned.toString()),
      });

      setEnableSaveButton(true);
      setSaveButtonError("");
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Error saving leaves");
    }
  };

  // New Joiner Tab Functions
  const fetchEmployees = async () => {
    if (!selectedYear || !/^\d{4}$/.test(String(selectedYear))) {
      setMessage("Please select a valid 4-digit year.");
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.get(`/api/employees-list/new-joiners/${selectedYear}`);
      const data = Array.isArray(response.data) ? response.data : response.data.results || [];
      setEmployees(data);
    } catch (error) {
      console.error("Error fetching employees:", error);
      setMessage("Error fetching employee list.");
    } finally {
      setIsLoading(false);
    }
  };

  const sortedEmployees = [...employees].sort((a, b) => a.Name.localeCompare(b.Name));

  const validateNewJoinerInputs = () => {
    const errors = {};
    const today = new Date().toISOString().split("T")[0];

    if (!selectedYear) errors.year = "Please select a year.";
    if (!addEmployeeId) errors.employeeId = "Please select an employee.";

    if (!joinDate) {
      errors.joinDate = "Joining date is required.";
    } else if (joinDate > today) {
      errors.joinDate = "Joining date cannot be in the future.";
    }

    if (addAllocatedUnplanned === "") {
      errors.unplannedLeave = "Unplanned leave is required.";
    } else if (isNaN(addAllocatedUnplanned) || Number(addAllocatedUnplanned) < 0) {
      errors.unplannedLeave = "Unplanned leave must be a non-negative number.";
    }

    if (addAllocatedPlanned === "") {
      errors.plannedLeave = "Planned leave is required.";
    } else if (isNaN(addAllocatedPlanned) || Number(addAllocatedPlanned) < 0) {
      errors.plannedLeave = "Planned leave must be a non-negative number.";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEmployeeIdChange = (e) => {
    const employeeId = e.target.value;
    setAddEmployeeId(employeeId);
    const employee = employees.find((emp) => emp.id === parseInt(employeeId));
    setSelectedEmployee(employee?.Name);
    clearValidationError("employeeId");
  };

  const addEmployeeLeave = async () => {
    setMessage(null);
    if (!validateNewJoinerInputs()) return;
    setShowConfirmation(true);
  };

  const confirmAddEmployeeLeave = async () => {
    try {
      await api.post("/api/employee-leaves-midyear", {
        employeeId: addEmployeeId,
        joinDate,
        year: selectedYear,
        allocatedUnplannedLeave: Number(addAllocatedUnplanned),
        allocatedPlannedLeave: Number(addAllocatedPlanned),
      });

      setJoinDate("");
      setAddAllocatedPlanned("");
      setAddAllocatedUnplanned("");
      setAddEmployeeId("");
      setValidationErrors({});
      setShowConfirmation(false);

      fetchEmployees();
      toast.success("Leave record added successfully!");
    } catch (error) {
      if (error.response) {
        setMessage(error.response.data.error || "Error adding leave record.");
      } else if (error.request) {
        setMessage("Error: Could not connect to server.");
      } else {
        setMessage("Error: Could not add leave record.");
      }
      console.error("Error adding leave record:", error);
    }
  };

  // Public Holidays Tab Functions
  const fetchHolidays = () =>
    fetch(`${HOLIDAY_API}/holidays`)
      .then((r) => r.json())
      .then(setHolidays)
      .catch((err) => {
        console.error(err);
        toast.error("Failed to load holidays.");
      });

  const fetchOffices = () =>
    fetch(`${HOLIDAY_API}/offices`)
      .then((r) => r.json())
      .then((data) => setOffices(data.map((o) => o.name)))
      .catch((err) => {
        console.error(err);
        toast.error("Failed to load offices.");
      });

  const handleAddOffice = () => {
    if (!newOfficeName.trim()) return;
    fetch(`${HOLIDAY_API}/offices`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: newOfficeName.trim() }),
    })
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then(() => {
        fetchOffices();
        toast.success(`Office "${newOfficeName}" added.`);
        setNewOfficeName("");
        setShowOfficeConfirm(false);
        setShowOfficeModal(false);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to add office.");
      });
  };

  const handleAddHoliday = () => {
    if (!newHoliday.date || !newHoliday.name || newHoliday.locations.length === 0) {
      setDateError("Please fill out date, name, and select at least one location.");
      setShowDateError(true);
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const sel = new Date(newHoliday.date);
    sel.setHours(0, 0, 0, 0);

    const minAllowed = new Date();
    minAllowed.setHours(0, 0, 0, 0);
    minAllowed.setDate(minAllowed.getDate() + 7);

    if (sel < minAllowed) {
      const formattedMin = minAllowed.toISOString().split("T")[0];
      setDateError(
        `Holidays must be scheduled at least 7 days in advance. Earliest allowed date: ${formattedMin}.`
      );
      setShowDateError(true);
      return;
    }

    Promise.all(
      newHoliday.locations.map((loc) =>
        fetch(`${HOLIDAY_API}/holidays`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            holiday_date: newHoliday.date,
            holiday_name: newHoliday.name,
            location: loc,
          }),
        }).then((r) => {
          if (!r.ok) throw new Error();
          return r.json();
        })
      )
    )
      .then(() => {
        fetchHolidays();
        toast.success(`Holiday "${newHoliday.name}" added.`);
        setNewHoliday({ date: "", name: "", locations: [] });
        setShowAddModal(false);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to add holiday.");
      });
  };

  const openEdit = (h) => {
    const d = new Date(h.holiday_date);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    setEditing({
      ...h,
      holiday_date: d.toISOString().split("T")[0],
      groupLocations: h.locations,
      locationMap: h.locationMap,
    });
    setShowEditModal(true);
  };

  const handleUpdate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const upd = new Date(editing.holiday_date);
    upd.setHours(0, 0, 0, 0);

    if (upd < today) {
      setDateError("Cannot set the holiday to a past date.");
      setShowDateError(true);
      return;
    }

    const minAllowed = new Date();
    minAllowed.setHours(0, 0, 0, 0);
    minAllowed.setDate(minAllowed.getDate() + 7);

    if (upd < minAllowed) {
      const formattedMin = minAllowed.toISOString().split("T")[0];
      setDateError(
        `Holidays must be scheduled at least 7 days in advance. Earliest allowed date: ${formattedMin}.`
      );
      setShowDateError(true);
      return;
    }

    const { groupLocations, locationMap, ...rest } = editing;
    const toDeleteLocs = groupLocations.filter((l) => !rest.locations.includes(l));
    const toUpdateLocs = groupLocations.filter((l) => rest.locations.includes(l));
    const toAddLocs = rest.locations.filter((l) => !groupLocations.includes(l));

    const calls = [
      ...toDeleteLocs.map((loc) =>
        fetch(`${HOLIDAY_API}/holidays/${locationMap[loc]}`, { method: "DELETE" })
      ),
      ...toUpdateLocs.map((loc) =>
        fetch(`${HOLIDAY_API}/holidays/${locationMap[loc]}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            holiday_date: rest.holiday_date,
            holiday_name: rest.holiday_name,
            location: loc,
            approval_status: "Pending",
            approved_by: "",
            approved_date: "",
          }),
        }).then((r) => r.json())
      ),
      ...toAddLocs.map((loc) =>
        fetch(`${HOLIDAY_API}/holidays`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            holiday_date: rest.holiday_date,
            holiday_name: rest.holiday_name,
            location: loc,
          }),
        }).then((r) => r.json())
      ),
    ];

    Promise.all(calls)
      .then(() => {
        fetchHolidays();
        toast.success(`Holiday "${rest.holiday_name}" updated.`);
        setShowEditModal(false);
        setEditing(null);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to update holiday.");
      });
  };

  const handleDelete = (h) => {
    setToDelete(h);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    Promise.all(
      toDelete.ids.map((id) =>
        fetch(`${HOLIDAY_API}/holidays/${id}`, { method: "DELETE" })
      )
    )
      .then(() => {
        fetchHolidays();
        toast.success(`Holiday "${toDelete.holiday_name}" deleted.`);
        setShowDeleteModal(false);
        setToDelete(null);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to delete holiday.");
      });
  };

  const handleApproveAll = () => {
    const pending = holidays.filter((h) => h.approval_status === "Pending");
    if (!pending.length) {
      toast.info("No pending holidays to approve.");
      return;
    }
    if (!window.confirm("Approve all pending holidays?")) return;
    const now = new Date().toISOString();
    Promise.all(
      pending.map((h) =>
        fetch(`${HOLIDAY_API}/holidays/approve/${h.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ approved_by: storedUser.name, approved_date: now }),
        }).then((r) => r.json())
      )
    )
      .then(() => {
        fetchHolidays();
        toast.success("All pending holidays approved!");
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to approve holidays.");
      });
  };

  // Group holidays by date+name
  const grouped = Object.values(
    holidays.reduce((acc, h) => {
      const key = `${h.holiday_date}-${h.holiday_name}`;
      if (!acc[key]) {
        acc[key] = {
          holiday_date: h.holiday_date,
          holiday_name: h.holiday_name,
          locations: new Set(),
          ids: [],
          locationMap: {},
          approval_status: h.approval_status,
        };
      }
      acc[key].locations.add(h.location);
      acc[key].ids.push(h.id);
      acc[key].locationMap[h.location] = h.id;
      return acc;
    }, {})
  ).map((x) => ({ ...x, locations: Array.from(x.locations) }));

  // Filter and sort holidays
  const filteredAndSortedHolidays = grouped
    .filter(holiday => {
      const matchesStatus = filterStatus === "all" ||
        (filterStatus === "pending" && holiday.approval_status === "Pending") ||
        (filterStatus === "approved" && holiday.approval_status === "Approved");

      const matchesSearch = holiday.holiday_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        holiday.locations.some(loc => loc.toLowerCase().includes(searchTerm.toLowerCase()));

      return matchesStatus && matchesSearch;
    })
    .sort((a, b) => {
      const dateA = new Date(a.holiday_date);
      const dateB = new Date(b.holiday_date);

      if (sortBy === "date") {
        return sortDirection === "asc" ? dateA - dateB : dateB - dateA;
      } else if (sortBy === "name") {
        return sortDirection === "asc"
          ? a.holiday_name.localeCompare(b.holiday_name)
          : b.holiday_name.localeCompare(a.holiday_name);
      } else if (sortBy === "status") {
        return sortDirection === "asc"
          ? a.approval_status.localeCompare(b.approval_status)
          : b.approval_status.localeCompare(a.approval_status);
      }
      return 0;
    });

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return <FaSort className="text-muted ms-1" />;
    return sortDirection === "asc"
      ? <FaSortUp className="text-primary ms-1" />
      : <FaSortDown className="text-primary ms-1" />;
  };

  const fetchLeavesData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/employee-leaves?year=${selectedYear}`);
      const data = response.data || {};

      const unplanned = data.allocatedUnplannedLeave || 0;
      const planned = data.allocatedPlannedLeave || 0;

      setAllocatedLeaves({
        unplanned: unplanned > 0 ? unplanned.toString() : "",
        planned: planned > 0 ? planned.toString() : "",
      });
      setAllocatedUnplannedLeave(unplanned > 0 ? unplanned.toString() : "");
      setAllocatedPlannedLeave(planned > 0 ? planned.toString() : "");

      if (unplanned > 0 || planned > 0) {
        setEnableSaveButton(true);
      } else {
        setEnableSaveButton(false);
      }
    } catch (error) {
      console.error("Error fetching leaves data:", error);
      setAllocatedLeaves({ unplanned: "", planned: "" });
      setAllocatedUnplannedLeave("");
      setAllocatedPlannedLeave("");
      setEnableSaveButton(false);
    }
  };

  useEffect(() => {
    if (enableSaveButton === true) {
      setSaveButtonError("Allocated leaves already saved for this year");
    } else {
      setSaveButtonError("");
    }
  }, [enableSaveButton]);

  useEffect(() => {
    fetchLeavesData();
  }, [selectedYear]);

  useEffect(() => {
    if (activeTab === "newJoiner") {
      fetchEmployees();
    } else if (activeTab === "holidays") {
      fetchHolidays();
      fetchOffices();
    }

    socketRef.current = io(API_URL, {
      transports: ["polling"],
      upgrade: false,
    });

    const refresh = () => {
      if (activeTab === "newJoiner") {
        fetchEmployees();
      } else if (activeTab === "holidays") {
        fetchHolidays();
      }
    };

    socketRef.current.on("employeeLeavesChanged", refresh);
    socketRef.current.on("leavesSaved", ({ unplanned, planned }) => {
      toast.info(`Realtime update: Unplanned ${unplanned}, Planned ${planned}`);
    });
    socketRef.current.on("holidaysChanged", fetchHolidays);
    socketRef.current.on("officesChanged", fetchOffices);

    return () => {
      socketRef.current.off("employeeLeavesChanged", refresh);
      socketRef.current.off("leavesSaved");
      socketRef.current.off("holidaysChanged");
      socketRef.current.off("officesChanged");
      socketRef.current.disconnect();
    };
  }, [selectedYear, activeTab]);

  return (
    <Container fluid>
      <Card className="shadow border-0">
        <Card.Body className="p-4">
          {/* Compact Year Selector */}
          <div className="mb-4">
            <Row className="align-items-center">
              <Col xs="auto">
                <h5 className="mb-0">
                  Select Year:
                </h5>
              </Col>
              <Col xs="auto">
                <div className="d-flex align-items-center">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setSelectedYear(prev => prev - 1)}
                    disabled={selectedYear <= yearOptions[0]}
                    className="me-1"
                  >
                    <FaChevronLeft />
                  </Button>
                  <Form.Select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    style={{ width: "120px" }}
                    size="sm"
                  >
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </Form.Select>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setSelectedYear(prev => prev + 1)}
                    disabled={selectedYear >= yearOptions[yearOptions.length - 1]}
                    className="ms-1"
                  >
                    <FaChevronRight />
                  </Button>
                </div>
              </Col>
            </Row>
          </div>

          {/* Simple Tabs */}
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-2"
          >
              <Tab
              eventKey="holidays"
              title={
                <div className="d-flex align-items-center">
                  <FaCalendarAlt className="me-2" />
                  Public Holidays
                  <Badge bg="info" className="ms-2">
                    {grouped.length}
                  </Badge>
                </div>
              }
            >
              <Row>
                <Col>
                  {/* Modern Header with Stats and Actions */}
                  <Card className="border-0 shadow-sm mb-4">
                    <Card.Body>
                      <Row className="align-items-center">
                        <Col md={6}>
                          <h4 className="mb-0 fw-bold">
                            <FaCalendarDay className="me-2 text-primary" />
                            Public Holidays Management
                          </h4>
                          <p className="text-muted mb-0">
                            Manage holidays across {offices.length} office locations
                          </p>
                        </Col>
                        <Col md={6} className="text-end">
                          <div className="d-flex justify-content-end gap-2">
                            <Button
                              variant="outline-primary"
                              onClick={() => setShowOfficeModal(true)}
                              className="d-flex align-items-center"
                            >
                              <FaBuilding className="me-2" />
                              Add Office
                            </Button>
                            <Button
                              variant="primary"
                              onClick={() => setShowAddModal(true)}
                              className="d-flex align-items-center"
                            >
                              <FaPlus className="me-2" />
                              Add Holiday
                            </Button>
                          </div>
                        </Col>
                      </Row>

                      {/* Stats Row */}
                      <Row className="mt-4">
                        <Col md={3}>
                          <Card className="border-0 bg-primary bg-opacity-10">
                            <Card.Body className="text-center">
                              <h3 className="fw-bold text-primary">{grouped.length}</h3>
                              <p className="mb-0 text-muted">Total Holidays</p>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={3}>
                          <Card className="border-0 bg-warning bg-opacity-10">
                            <Card.Body className="text-center">
                              <h3 className="fw-bold text-warning">
                                {grouped.filter(h => h.approval_status === "Pending").length}
                              </h3>
                              <p className="mb-0 text-muted">Pending Approval</p>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={3}>
                          <Card className="border-0 bg-success bg-opacity-10">
                            <Card.Body className="text-center">
                              <h3 className="fw-bold text-success">
                                {grouped.filter(h => h.approval_status === "Approved").length}
                              </h3>
                              <p className="mb-0 text-muted">Approved</p>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={3}>
                          <Card className="border-0 bg-info bg-opacity-10">
                            <Card.Body className="text-center">
                              <h3 className="fw-bold text-info">
                                {offices.length}
                              </h3>
                              <p className="mb-0 text-muted">Office Locations</p>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* Modern Table */}
                  <Card className="mt-4 shadow-sm">
                    <Card.Body className="p-0">
                      {grouped.length > 0 ? (
                        <Table hover responsive bordered className="mb-0">
                          <thead className="table-dark text-center">
                            <tr>
                              <th>Date</th>
                              <th>Holiday</th>
                              {offices.map((o) => (
                                <th key={o}>{o}</th>
                              ))}
                              <th>Actions</th>
                              <th>Approval</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {grouped.map((h, idx) => {
                              const hd = new Date(h.holiday_date);
                              const only = new Date(hd.getFullYear(), hd.getMonth(), hd.getDate());
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              const isPast = only < today;
                              // lock if past OR within 7 days from today
                              const threshold = new Date(today);
                              threshold.setDate(threshold.getDate() + 7);
                              const isLocked = only < threshold;
                              const formatted = hd.toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              });
                              return (
                                <tr key={idx} className={isPast ? "past-row" : ""}>
                                  <td className="text-center align-middle">{formatted}</td>
                                  <td className="align-middle">{h.holiday_name}</td>
                                  {offices.map((o) => (
                                    <td key={o} className="text-center align-middle">
                                      {h.locations.includes(o) && "✓"}
                                    </td>
                                  ))}
                                  <td className="text-center align-middle">
                                    <FaPencilAlt
                                      onClick={() => !isLocked && openEdit(h)}
                                      className={isLocked ? "text-muted me-2" : "text-primary me-2"}
                                      style={{ cursor: isLocked ? "not-allowed" : "pointer" }}
                                    />
                                    <FaTrash
                                      onClick={() => !isLocked && handleDelete(h)}
                                      className={isLocked ? "text-muted" : "text-danger"}
                                      style={{ cursor: isLocked ? "not-allowed" : "pointer" }}
                                    />
                                  </td>
                                  <td className="text-center align-middle">
                                    {h.approval_status === "Pending" ? (
                                      <Badge bg="warning">Pending</Badge>
                                    ) : (
                                      <Badge bg="success">Approved</Badge>
                                    )}
                                  </td>
                                  <td className="text-center align-middle">
                                    {isPast ? (
                                      <Badge bg="secondary">Completed</Badge>
                                    ) : (
                                      <Badge bg="info">Upcoming</Badge>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      ) : (
                        <p className="text-center py-4">No holidays found.</p>
                      )}
                    </Card.Body>
                    <Card.Footer className="bg-light border-0">
                      <Row className="align-items-center">
                        <Col md={6}>
                          <div className="text-muted">
                            Showing {filteredAndSortedHolidays.length} of {grouped.length} holidays
                          </div>
                        </Col>
                        <Col md={6} className="text-end">
                          <Button
                            variant="success"
                            onClick={handleApproveAll}
                            className="d-flex align-items-center ms-auto"
                          >
                            <FaCheck className="me-2" />
                            Approve All Pending
                          </Button>
                        </Col>
                      </Row>
                    </Card.Footer>
                  </Card>
                </Col>
              </Row>
            </Tab>
            <Tab
              eventKey="annual"
              title={
                <div className="d-flex align-items-center">
                  <FaCog className="me-2" />
                  Annual Leaves
                  <Badge bg="primary" className="ms-2">
                    {allocatedLeaves.unplanned || "0"}/{allocatedLeaves.planned || "0"}
                  </Badge>
                </div>
              }
            >
              <Row className="justify-content-center mt-3">
                <Col lg={8}>
                  {message && (
                    <Alert variant="info" onClose={() => setMessage(null)} dismissible className="mb-3">
                      {message}
                    </Alert>
                  )}

                  <Card className="border">
                    <Card.Body>
                      <h5 className="mb-4">Configure Annual Leaves for {selectedYear}</h5>

                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-semibold">
                              Allocated Unplanned Leaves
                            </Form.Label>
                            <Form.Control
                              type="number"
                              placeholder="Enter unplanned leaves"
                              value={parseInt(allocatedUnplannedLeave.toString()) || ""}
                              onChange={(e) =>
                                setAllocatedUnplannedLeave(handleValueChange(e.target.value))
                              }
                              min="0"
                              max="999"
                            />
                            <Form.Text className="text-muted">
                              Casual leaves for emergencies
                            </Form.Text>
                          </Form.Group>
                        </Col>

                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-semibold">
                              Allocated Planned Leaves
                            </Form.Label>
                            <Form.Control
                              type="number"
                              placeholder="Enter planned leaves"
                              value={parseInt(allocatedPlannedLeave.toString()) || ""}
                              onChange={(e) =>
                                setAllocatedPlannedLeave(handleValueChange(e.target.value))
                              }
                              min="0"
                              max="999"
                            />
                            <Form.Text className="text-muted">
                              Pre-planned vacation days
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>

                      {/* Total Leaves Display */}
                      <div className="mt-4 p-3 bg-light rounded text-center">
                        <div className="d-flex justify-content-center align-items-center">
                          <FaLeaf className="me-3 text-primary" size={24} />
                          <div>
                            <div className="text-muted small">Total Annual Leaves</div>
                            <h3 className="mb-0 fw-bold">{totalLeaves}</h3>
                            <div className="small text-muted">
                              = {allocatedUnplannedLeave || "0"} Unplanned + {allocatedPlannedLeave || "0"} Planned
                            </div>
                          </div>
                        </div>
                      </div>

                      {saveButtonError && (
                        <Alert variant="warning" className="mt-3 py-2">
                          <FaExclamationCircle className="me-2" />
                          {saveButtonError}
                        </Alert>
                      )}

                      <div className="text-center mt-4">
                        <Button
                          variant="primary"
                          onClick={handleSaveAnnualLeaves}
                          size="lg"
                          disabled={enableSaveButton}
                          className="px-5"
                        >
                          <FaSave className="me-2" />
                          {enableSaveButton ? "Leaves Already Saved" : "Save Annual Leaves"}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab>

            <Tab
              eventKey="newJoiner"
              title={
                <div className="d-flex align-items-center">
                  <FaUserPlus className="me-2" />
                  New Joiner Leaves
                  <Badge bg="success" className="ms-2">
                    {employees.length}
                  </Badge>
                </div>
              }
            >
              <Row className="mt-3">
                <Col lg={8}>
                  <Card className="border">
                    <Card.Body>
                      <h5 className="mb-4">Add Leaves for New Joiner</h5>

                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-semibold">
                              Select Employee
                            </Form.Label>
                            <Form.Control
                              as="select"
                              value={addEmployeeId}
                              onChange={handleEmployeeIdChange}
                              isInvalid={!!validationErrors.employeeId}
                              disabled={isLoading}
                            >
                              <option value="">
                                {isLoading ? "Loading employees..." : "Select an employee"}
                              </option>
                              {sortedEmployees.map((emp) => (
                                <option key={emp.id} value={emp.id}>
                                  {emp.Name}
                                </option>
                              ))}
                            </Form.Control>
                            {validationErrors.employeeId && (
                              <Form.Text className="text-danger">
                                {validationErrors.employeeId}
                              </Form.Text>
                            )}
                            {isLoading && (
                              <div className="mt-2 text-center">
                                <Spinner animation="border" variant="primary" size="sm" />
                                <span className="ms-2">Loading employees...</span>
                              </div>
                            )}
                          </Form.Group>
                        </Col>

                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-semibold">
                              Joining Date
                            </Form.Label>
                            <Form.Control
                              type="date"
                              value={joinDate}
                              onChange={(e) => {
                                setJoinDate(e.target.value);
                                clearValidationError("joinDate");
                              }}
                              isInvalid={!!validationErrors.joinDate}
                            />
                            {validationErrors.joinDate && (
                              <Form.Text className="text-danger">
                                {validationErrors.joinDate}
                              </Form.Text>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row className="g-3 mt-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-semibold">
                              Allocated Unplanned Leave
                            </Form.Label>
                            <Form.Control
                              type="number"
                              value={addAllocatedUnplanned}
                              onChange={(e) => {
                                setAddAllocatedUnplanned(e.target.value);
                                clearValidationError("unplannedLeave");
                              }}
                              min="0"
                              isInvalid={!!validationErrors.unplannedLeave}
                            />
                            {validationErrors.unplannedLeave && (
                              <Form.Text className="text-danger">
                                {validationErrors.unplannedLeave}
                              </Form.Text>
                            )}
                          </Form.Group>
                        </Col>

                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-semibold">
                              Allocated Planned Leave
                            </Form.Label>
                            <Form.Control
                              type="number"
                              value={addAllocatedPlanned}
                              onChange={(e) => {
                                setAddAllocatedPlanned(e.target.value);
                                clearValidationError("plannedLeave");
                              }}
                              min="0"
                              isInvalid={!!validationErrors.plannedLeave}
                            />
                            {validationErrors.plannedLeave && (
                              <Form.Text className="text-danger">
                                {validationErrors.plannedLeave}
                              </Form.Text>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>

                      <div className="text-center mt-4">
                        <Button
                          variant="success"
                          onClick={addEmployeeLeave}
                          size="lg"
                          className="px-5"
                        >
                          <FaUserPlus className="me-2" />
                          Add Leave Record
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>

      {/* Modern Modals */}
      {/* Save Leaves Modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
        size="lg"
      >
        <Modal.Header className="border-0 bg-primary text-white">
          <Modal.Title className="fw-bold">
            <FaSave className="me-2" />
            Confirm Save Leaves
          </Modal.Title>
          <Button
            variant="link"
            className="text-white p-0"
            onClick={() => setShowConfirmModal(false)}
            style={{ fontSize: '1.5rem' }}
          >
            ×
          </Button>
        </Modal.Header>
        <Modal.Body className="py-4">
          <div className="text-center mb-4">
            <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex p-4 mb-3">
              <FaCheckCircle size={60} className="text-primary" />
            </div>
            <h4 className="fw-bold">Save leaves for {selectedYear}?</h4>
            <p className="text-muted">Please review the leave allocation details below</p>
          </div>

          <Card className="mt-4 shadow-sm">
            <Card.Body className="p-0">
              {grouped.length > 0 ? (
                <Table hover responsive bordered className="mb-0">
                  <thead className="table-dark text-center">
                    <tr>
                      <th>Date</th>
                      <th>Holiday</th>
                      {offices.map((o) => (
                        <th key={o}>{o}</th>
                      ))}
                      <th>Actions</th>
                      <th>Approval</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped.map((h, idx) => {
                      const hd = new Date(h.holiday_date);
                      const only = new Date(hd.getFullYear(), hd.getMonth(), hd.getDate());
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const isPast = only < today;
                      // lock if past OR within 7 days from today
                      const threshold = new Date(today);
                      threshold.setDate(threshold.getDate() + 7);
                      const isLocked = only < threshold;
                      const formatted = hd.toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      });
                      return (
                        <tr key={idx} className={isPast ? "past-row" : ""}>
                          <td className="text-center align-middle">{formatted}</td>
                          <td className="align-middle">{h.holiday_name}</td>
                          {offices.map((o) => (
                            <td key={o} className="text-center align-middle">
                              {h.locations.includes(o) && "✓"}
                            </td>
                          ))}
                          <td className="text-center align-middle">
                            <FaPencilAlt
                              onClick={() => !isLocked && openEdit(h)}
                              className={isLocked ? "text-muted me-2" : "text-primary me-2"}
                              style={{ cursor: isLocked ? "not-allowed" : "pointer" }}
                            />
                            <FaTrash
                              onClick={() => !isLocked && handleDelete(h)}
                              className={isLocked ? "text-muted" : "text-danger"}
                              style={{ cursor: isLocked ? "not-allowed" : "pointer" }}
                            />
                          </td>
                          <td className="text-center align-middle">
                            {h.approval_status === "Pending" ? (
                              <Badge bg="warning">Pending</Badge>
                            ) : (
                              <Badge bg="success">Approved</Badge>
                            )}
                          </td>
                          <td className="text-center align-middle">
                            {isPast ? (
                              <Badge bg="secondary">Completed</Badge>
                            ) : (
                              <Badge bg="info">Upcoming</Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              ) : (
                <p className="text-center py-4">No holidays found.</p>
              )}
            </Card.Body>
          </Card>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button
            variant="outline-secondary"
            onClick={() => setShowConfirmModal(false)}
            className="px-4"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={confirmSaveAnnualLeaves}
            className="px-4"
          >
            <FaSave className="me-2" />
            Confirm Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* New Joiner Confirmation Modal */}
      <Modal
        show={showConfirmation}
        onHide={() => setShowConfirmation(false)}
        centered
      >
        <Modal.Header className="border-0 bg-success text-white">
          <Modal.Title className="fw-bold">
            <FaUserPlus className="me-2" />
            Confirm Leave Allocation
          </Modal.Title>
          <Button
            variant="link"
            className="text-white p-0"
            onClick={() => setShowConfirmation(false)}
            style={{ fontSize: '1.5rem' }}
          >
            ×
          </Button>
        </Modal.Header>
        <Modal.Body className="py-4">
          <div className="text-center mb-4">
            <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex p-4 mb-3">
              <FaUserPlus size={60} className="text-success" />
            </div>
            <h4 className="fw-bold">Add leave allocation for new joiner?</h4>
            <p className="text-muted">Review the details below before confirming</p>
          </div>

          <Card className="border-0 bg-light">
            <Card.Body>
              <div className="mb-3">
                <label className="text-muted small mb-1">Employee</label>
                <div className="fw-bold fs-5">{selectedEmployee || "N/A"}</div>
              </div>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="text-muted small mb-1">Year</label>
                    <div className="fw-bold">{selectedYear}</div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="text-muted small mb-1">Joining Date</label>
                    <div className="fw-bold">{joinDate}</div>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="text-muted small mb-1">Unplanned Leave</label>
                    <div className="fw-bold text-warning fs-4">{addAllocatedUnplanned} days</div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="text-muted small mb-1">Planned Leave</label>
                    <div className="fw-bold text-info fs-4">{addAllocatedPlanned} days</div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button
            variant="outline-secondary"
            onClick={() => setShowConfirmation(false)}
            className="px-4"
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={confirmAddEmployeeLeave}
            className="px-4"
          >
            <FaCheck className="me-2" />
            Confirm Allocation
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Office Modal */}
      <Modal show={showOfficeModal} onHide={() => setShowOfficeModal(false)} centered>
        <Modal.Header className="border-0 bg-info text-white">
          <Modal.Title className="fw-bold">
            <FaBuilding className="me-2" />
            Add New Office
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-4">
          <Form.Group>
            <Form.Label className="fw-semibold">Office Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter office name (e.g., New York Office)"
              value={newOfficeName}
              onChange={(e) => setNewOfficeName(e.target.value)}
              className="py-2"
            />
            
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button
            variant="outline-secondary"
            onClick={() => setShowOfficeModal(false)}
            className="px-4"
          >
            Cancel
          </Button>
          <Button
            variant="info"
            onClick={() => setShowOfficeConfirm(true)}
            className="px-4"
            disabled={!newOfficeName.trim()}
          >
            <FaPlus className="me-2" />
            Add Office
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirm Add Office Modal */}
      <Modal show={showOfficeConfirm} onHide={() => setShowOfficeConfirm(false)} centered>
        <Modal.Header className="border-0 bg-info text-white">
          <Modal.Title className="fw-bold">Confirm Add Office</Modal.Title>
          <Button
            variant="link"
            className="text-white p-0"
            onClick={() => setShowOfficeConfirm(false)}
            style={{ fontSize: '1.5rem' }}
          >
            ×
          </Button>
        </Modal.Header>
        <Modal.Body className="py-4 text-center">
          <FaBuilding size={48} className="text-info mb-3" />
          <h5 className="fw-bold mb-3">Add "{newOfficeName}" as a new office?</h5>
          <p className="text-muted">
            This office will be available for holiday assignments. You can add holidays specific to this location.
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button
            variant="outline-secondary"
            onClick={() => setShowOfficeConfirm(false)}
            className="px-4"
          >
            Cancel
          </Button>
          <Button
            variant="info"
            onClick={handleAddOffice}
            className="px-4"
          >
            <FaCheck className="me-2" />
            Confirm Add
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Holiday Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered size="lg">
        <Modal.Header className="border-0 bg-primary text-white">
          <Modal.Title className="fw-bold">
            <FaCalendarPlus className="me-2" />
          Create a new public holiday
          </Modal.Title>
    
        </Modal.Header>
        <Modal.Body className="py-4">

          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  <FaCalendarAlt className="me-2" />
                  Holiday Date
                </Form.Label>
                <Form.Control
                  type="date"
                  value={newHoliday.date}
                  onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
                  className="py-2"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  <FaCalendarDay className="me-2" />
                  Holiday Name
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., Christmas Day, New Year's Day"
                  value={newHoliday.name}
                  onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
                  className="py-2"
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mt-4">
            <Form.Label className="fw-semibold">
              <FaMapMarkerAlt className="me-2" />
              Select Locations
            </Form.Label>
            <ModernLocationSelect
              options={offices}
              selectedLocations={newHoliday.locations}
              setSelectedLocations={(locs) => setNewHoliday({ ...newHoliday, locations: locs })}
            />
            <Form.Text className="text-muted">
              Select one or more office locations where this holiday applies.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button
            variant="outline-secondary"
            onClick={() => setShowAddModal(false)}
            className="px-4"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddHoliday}
            className="px-4"
            disabled={!newHoliday.date || !newHoliday.name || newHoliday.locations.length === 0}
          >
            <FaPlus className="me-2" />
            Add Holiday
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Holiday Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg">
        <Modal.Header className="border-0 bg-warning text-white">
          <Modal.Title className="fw-bold">
            <FaPencilAlt className="me-2" />
            Edit Holiday
          </Modal.Title>
        </Modal.Header>
        {editing && (
          <Modal.Body className="py-4">
        
            <Form>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <FaCalendarAlt className="me-2" />
                      Holiday Date
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="holiday_date"
                      value={editing.holiday_date}
                      onChange={(e) => setEditing({ ...editing, holiday_date: e.target.value })}
                      className="py-2"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      <FaCalendarDay className="me-2" />
                      Holiday Name
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="holiday_name"
                      value={editing.holiday_name}
                      onChange={(e) => setEditing({ ...editing, holiday_name: e.target.value })}
                      className="py-2"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mt-4">
                <Form.Label className="fw-semibold">
                  <FaMapMarkerAlt className="me-2" />
                  Locations
                </Form.Label>
                <ModernLocationSelect
                  options={offices}
                  selectedLocations={editing.locations}
                  setSelectedLocations={(locs) => setEditing({ ...editing, locations: locs })}
                />
                <Form.Text className="text-muted">
                  Update which office locations this holiday applies to.
                </Form.Text>
              </Form.Group>
            </Form>
          </Modal.Body>
        )}
        <Modal.Footer className="border-0">
          <Button
            variant="outline-secondary"
            onClick={() => setShowEditModal(false)}
            className="px-4"
          >
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={handleUpdate}
            className="px-4 text-white"
          >
            <FaSave className="me-2" />
            Update Holiday
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Holiday Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header className="border-0 bg-danger text-white">
          <Modal.Title className="fw-bold">
            <FaTrash className="me-2" />
            Delete Holiday
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-4 text-center">
          <div className="bg-danger bg-opacity-10 rounded-circle d-inline-flex p-4 mb-3">
            <FaTrash size={48} className="text-danger" />
          </div>
          <h5 className="fw-bold mb-3">Delete "{toDelete?.holiday_name}"?</h5>
          <p className="text-muted">
            This will permanently remove the holiday from all selected locations.
            This action cannot be undone.
          </p>
          {toDelete && (
            <Card className="border-0 bg-light mt-3">
              <Card.Body>
                <div className="text-start">
                  <div className="mb-2">
                    <span className="text-muted">Date:</span>{" "}
                    <strong>{new Date(toDelete.holiday_date).toLocaleDateString()}</strong>
                  </div>
                  <div className="mb-2">
                    <span className="text-muted">Locations:</span>{" "}
                    <strong>{toDelete.locations.join(", ")}</strong>
                  </div>
                </div>
              </Card.Body>
            </Card>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button
            variant="outline-secondary"
            onClick={() => setShowDeleteModal(false)}
            className="px-4"
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmDelete}
            className="px-4"
          >
            <FaTrash className="me-2" />
            Delete Permanently
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Date Error Modal */}
      <Modal show={showDateError} onHide={() => setShowDateError(false)} centered>
        <Modal.Header className="border-0 bg-warning text-white">
          <Modal.Title className="fw-bold">
            <FaExclamationCircle className="me-2" />
            Invalid Date
          </Modal.Title>
          <Button
            variant="link"
            className="text-white p-0"
            onClick={() => setShowDateError(false)}
            style={{ fontSize: '1.5rem' }}
          >
            ×
          </Button>
        </Modal.Header>
        <Modal.Body className="py-4">
          <div className="d-flex align-items-start">
            <FaExclamationCircle size={32} className="text-warning me-3 flex-shrink-0" />
            <div>
              <h5 className="fw-bold mb-2">Date Restriction</h5>
              <p className="mb-0">{dateError}</p>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button
            variant="warning"
            onClick={() => setShowDateError(false)}
            className="px-4 text-white"
          >
            <FaCheck className="me-2" />
            Understood
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="top-right" />
    </Container>
  );
};

export default AnnualLeaves;